// FLAGS: personal-data
// One session's fields, including its assigned experts.
import { useState } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import { cn } from "@openup/shared/lib/utils";
import { Button } from "@openup/shared/primitives/ui/button";
import { Input } from "@openup/shared/primitives/ui/input";
import { Textarea } from "@openup/shared/primitives/ui/textarea";
import { Card, CardContent } from "@openup/shared/primitives/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@openup/shared/primitives/ui/collapsible";
import { HostableExpert, SpaceEventType } from "@src/models/spaces";
import FormField from "@src/components/spaces/FormField";
import SessionExperts from "@src/components/spaces/SessionExperts";
import SessionLengthField from "@src/components/spaces/SessionLengthField";
import { Select } from "@src/components/spaces/ui";
import {
  SessionErrors,
  SessionFormState,
  sessionSummary,
} from "@src/components/spaces/createEventForm";
import { TIMEZONES } from "@src/components/spaces/timezones";

interface SessionCardProps {
  index: number;
  session: SessionFormState;
  eventType: SpaceEventType;
  errors?: SessionErrors;
  experts: HostableExpert[];
  canRemove: boolean;
  onChange: (next: SessionFormState) => void;
  onRemove: () => void;
}

const SessionCard = ({
  index,
  session,
  eventType,
  errors,
  experts,
  canRemove,
  onChange,
  onRemove,
}: SessionCardProps) => {
  const [open, setOpen] = useState(true);
  // A card with validation errors stays open so the errors are never hidden.
  const isOpen = open || Boolean(errors && Object.values(errors).some(Boolean));
  const patch = (partial: Partial<SessionFormState>) =>
    onChange({ ...session, ...partial });

  return (
    <Card>
      <CardContent className="p-5">
        <Collapsible open={isOpen} onOpenChange={setOpen}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="min-w-0 flex-1">
              <CollapsibleTrigger className="flex w-full items-center gap-2 rounded text-left">
                <ChevronDown
                  aria-hidden
                  className={cn(
                    "h-4 w-4 shrink-0 text-gray-500 transition-transform",
                    !isOpen && "-rotate-90",
                  )}
                />
                <span className="shrink-0 text-base font-semibold text-indigo-800">
                  Session {index + 1}
                </span>
                {!isOpen && (
                  <span className="min-w-0 truncate text-sm text-gray-600">
                    {sessionSummary(session)}
                  </span>
                )}
              </CollapsibleTrigger>
            </h3>
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                aria-label={`Remove session ${index + 1}`}
              >
                <Trash2 className="mr-1 h-4 w-4" aria-hidden />
                Remove
              </Button>
            )}
          </div>
          {/* Clip only while collapsing/closed (for the height animation); when open, overflow must
              be visible so the experts dropdown can extend past the card instead of being cut off. */}
          <CollapsibleContent className="data-[state=closed]:overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <div className="mb-4 mt-4">
              <FormField label="Session name">
                {({ id }) => (
                  <Input
                    id={id}
                    value={session.name}
                    placeholder="Optional — defaults to the event title"
                    onChange={(e) => patch({ name: e.target.value })}
                  />
                )}
              </FormField>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Date" required error={errors?.date}>
                {({ id, invalid }) => (
                  <Input
                    id={id}
                    type="date"
                    value={session.date}
                    aria-invalid={invalid}
                    onChange={(e) => patch({ date: e.target.value })}
                  />
                )}
              </FormField>

              <FormField label="Start time" required error={errors?.startTime}>
                {({ id, invalid }) => (
                  <Input
                    id={id}
                    type="time"
                    value={session.startTime}
                    aria-invalid={invalid}
                    onChange={(e) => patch({ startTime: e.target.value })}
                  />
                )}
              </FormField>

              <SessionLengthField
                session={session}
                error={errors?.endTime}
                onPatch={patch}
              />

              <FormField label="Timezone">
                {({ id }) => (
                  <Select
                    id={id}
                    value={session.timeZone}
                    onChange={(e) => patch({ timeZone: e.target.value })}
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>

              <FormField label="Capacity" error={errors?.capacity}>
                {({ id, invalid }) => (
                  <Input
                    id={id}
                    type="number"
                    min={1}
                    value={session.capacity}
                    aria-invalid={invalid}
                    onChange={(e) => patch({ capacity: e.target.value })}
                  />
                )}
              </FormField>
            </div>

            <div className="mt-4">
              <FormField label="Session instructions">
                {({ id }) => (
                  <Textarea
                    id={id}
                    rows={3}
                    maxLength={2000}
                    value={session.sessionInstructions}
                    placeholder="Optional — practical notes for attendees, e.g. what to prepare"
                    onChange={(e) =>
                      patch({ sessionInstructions: e.target.value })
                    }
                  />
                )}
              </FormField>
              <p className="mt-1 text-xs text-gray-500">
                Included in the confirmation and reminder emails sent to
                registered attendees.
              </p>
            </div>

            <SessionExperts
              assigned={session.experts}
              experts={experts}
              eventType={eventType}
              error={errors?.experts}
              onChange={(next) => patch({ experts: next })}
            />
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};

export default SessionCard;
